import { useMutation, useQuery } from '@tanstack/react-query'
import apiV2 from '../../service/apiV2.service'
import { reactQueryErrorHandler } from '@dg-util'
import { FSMService } from '../../FSM'
import { PartyV2Type, ResponseErrorV2Type } from '../../types/ApiV2'
import queryClient from '../../queryClient'
import useReactQueryErrorEffect from './useReactQueryErrorEffect'

type UseReactQueryPartyArgsType = {
  // Default Get Own Party fetching enabled value
  partyQueryEnabled?: boolean
}

const useReactQueryParty = (options: UseReactQueryPartyArgsType = {}) => {
  const { partyQueryEnabled = true } = options

  // Update Mutation
  const {
    mutateAsync: updateParty,
    isPending: isUpdatePartyLoading,
    data: updatedPartyData,
    isError: isUpdatePartyError,
    error: updatePartyError,
  } = useMutation({
    mutationFn: (data: Partial<PartyV2Type>) => apiV2.updateParty(data),
    async onSuccess(response) {
      // Set party data in query cache
      queryClient.setQueryData(['party'], (party: Partial<PartyV2Type> | undefined) => ({
        ...party,
        ...response,
      }))
    },
    onError: (error: ResponseErrorV2Type) => reactQueryErrorHandler(error, FSMService.flowService),
  })

  // Get Own Party Query
  const {
    data: party,
    isLoading: isGetPartyLoading,
    isError: isPartyError,
    error: partyError,
  } = useQuery({
    queryKey: ['party'],
    queryFn: () => apiV2.getPartyOwn(),
    select: (response) => response.data,
    enabled: partyQueryEnabled,
  })

  useReactQueryErrorEffect(partyError)

  return {
    party,
    isLoading: isUpdatePartyLoading || isGetPartyLoading,
    updateParty,
    updatedPartyData,
    isUpdatePartyError,
    updatePartyError,
    isPartyError,
    partyError,
  }
}

export default useReactQueryParty
